// @file For rendering Tldraw canvas in Vue component
import { yieldToMain } from '@@/bits/global'
import { createBridgeToReactState } from '@@/bits/vue_react_bridge'
import { ShownCursorType } from '@@/enums'
import { useWhiteboardCollaborationStore } from '@@/pinia/whiteboard_collaboration'
import { useWhiteboardModeStore } from '@@/pinia/whiteboard_mode'
import { useWhiteboardPermissionsStore } from '@@/pinia/whiteboard_permissions'
import { useWhiteboardConnectionUri } from '@@/vuecomposables/useWhiteboardConnectionUri'
import { storeToRefs } from 'pinia'
import { createRoot } from 'react-dom/client'
import type { Editor } from 'tldraw'
import { computed, onMounted } from 'vue'

const useTldrawCanvas = ({ elementId, onMount }: { elementId: string; onMount: (editor: Editor) => void }): void => {
  const whiteboardPermissionsStore = useWhiteboardPermissionsStore()
  const { isReadOnlyMode } = storeToRefs(useWhiteboardModeStore())
  const { shownCursorType } = storeToRefs(useWhiteboardCollaborationStore())
  const { getConnectionUri } = useWhiteboardConnectionUri()

  const bridgedShownCursorType = createBridgeToReactState(
    computed<ShownCursorType>(() => {
      if (isReadOnlyMode.value) return ShownCursorType.NONE
      return shownCursorType.value
    }),
  )

  onMounted(async () => {
    if (elementId == null) {
      throw new Error('Element id is required')
    }
    const domNode = document.getElementById(elementId) as HTMLElement
    if (domNode == null) {
      throw new Error(`Element with id ${elementId} not found`)
    }

    // Dynamically import DrawingCanvas to enable code splitting
    const { WhiteboardDrawingCanvas } = await import('@@/reactcomponents/WhiteboardDrawingCanvas')

    await yieldToMain()
    const root = createRoot(domNode)
    root.render(
      <WhiteboardDrawingCanvas
        onMount={onMount}
        canIViewShape={whiteboardPermissionsStore.canIViewShape}
        useShownCursorType={bridgedShownCursorType}
        getConnectionUri={getConnectionUri}
      />,
    )
  })
}

export { useTldrawCanvas }
